import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isUndefined from 'lodash/isUndefined';
import { bindActionCreators } from 'redux';
import {
  Grid, AppBar, Tabs, Tab, Badge,
} from '@mui/material';
import {
  DirectionsBike, Layers, Place,
  Phone, People, Directions,
  LocalShipping, Settings, ShowChart,
} from '@mui/icons-material';
import Wrench from '@mui/icons-material/Build';

import fieldReportActions from '../../actions/fieldReports';
import vehicleActions from '../../actions/vehicles';
import marketActions from '../../actions/markets';
import DashboardPage from './dashboard-page';
import MarketEditPage from './market-edit-page';
import RepairTicketsSummary from './repair-tickets-table';
import UserTable from './user-table';
import LocationTable from './location-table';
import MarketVehicleSummary from './market-vehicle-summary';
import ZonesTable from './zone-table';
import FieldReportTable from './fieldReport-table';
import { ACTIONS, PAGES } from '../../access';
import { getMatchingMarket, FieldReportStatuses, FeatureFlagTypes } from '../../lib/utils';
import VehicleZonesContainer from './vehicle-zones';
import MarketRunTables from './runs/market-run-tables';

const VEHICLES_TAB = 'vehicles';
const REPAIRS_TAB = 'repairs';
const ALERTS_TAB = 'alerts';
const FIELD_TAB = 'fieldreports';
const LOCATIONS_TAB = 'locations';
const ZONES_TAB = 'zones';
const VEHICLE_ZONES_TAB = 'vehicleZones';
const RUN_MANAGER_TAB = 'runs';
const MAP_TAB = 'map';
const USERS_TAB = 'users';
const SETTINGS_TAB = 'settings';
const DASHBOARD_TAB = 'dashboard';

const styles = {
  shareHeader: {
    textAlign: 'center',
    fontSize: '20px',
    color: 'black',
  },
  tab: {
    minWidth: 110,
    width: 110,
  },
};

/* eslint-disable object-curly-newline */
function MARKET_TAB_DATA(newFieldReports) {
  const data = [
    { access: PAGES.DASHBOARD, value: DASHBOARD_TAB, label: 'Dashboard', icon: <ShowChart /> },
    { access: PAGES.MARKET_VEHICLE_SUMMARY, value: VEHICLES_TAB, label: 'Vehicles', icon: <DirectionsBike /> },
    { access: PAGES.REPAIR_TICKETS_SUMMARY, value: REPAIRS_TAB, label: 'Repairs', icon: <Wrench /> },
    { access: PAGES.FIELD_REPORT_TABLE, value: FIELD_TAB, label: 'Cases', icon: <Badge badgeContent={newFieldReports.length} color="error" overlap="rectangular"><Phone /></Badge> },
    { access: PAGES.LOCATION_TABLE, value: LOCATIONS_TAB, label: 'Locations', icon: <Place /> },
    { access: PAGES.VEHICLE_ZONES_PAGE, value: VEHICLE_ZONES_TAB, label: 'Vehicle Zones', icon: <Layers /> },
    { access: PAGES.ZONES_TABLE, value: ZONES_TAB, label: 'Routes', icon: <Directions /> },
    { access: PAGES.RUN_MANAGER, value: RUN_MANAGER_TAB, label: 'Run', icon: <LocalShipping /> },
    { access: PAGES.USER_TABLE, value: USERS_TAB, label: 'Workers', icon: <People /> },
    { access: PAGES.MARKET_SETTINGS, value: SETTINGS_TAB, label: 'Settings', icon: <Settings /> },
  ];
  return data;
}

/* eslint-enable object-curly-newline */

class MarketTabs extends React.Component {
  constructor(props) {
    super(props);

    const { value: initialValue } = props;

    this.state = {
      value: initialValue || DASHBOARD_TAB,
      isLoadingMarkets: false,
    };

    this.getRoute = this.getRoute.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  componentDidMount() {
    const {
      fetchFieldReports,
    } = this.props;

    const { marketId } = get(this, 'props.match.params');
    if (marketId) {
      fetchFieldReports({ market_id: marketId });
    }
  }

  componentDidUpdate() {
    const {
      markets, selectedMarket, fetchMarkets, selectMarket,
    } = this.props;
    const { isLoadingMarkets } = this.state;
    const { marketId } = get(this, 'props.match.params');

    // if there wasn't a selected market, there aren't any markets: fetch markets
    if (!selectedMarket && !markets && !isLoadingMarkets) {
      this.setState(() => {
        fetchMarkets().then(() => this.setState(() => ({ isLoadingMarkets: false })));
        return { isLoadingMarkets: true };
      });
      return;
    }

    // if there wasn't a selected market, but there are markets: select a market
    if (!selectedMarket && markets && markets.length) {
      const matchingMarket = getMatchingMarket(markets, marketId);
      return selectMarket(matchingMarket);
    }
  }

  componentWillUnmount() {
    this.props.selectMarket();
  }

  getFeatureFlags = (market) => {
    const flags = get(market, 'featureFlags', []);
    if (!flags || flags.length === 0) {
      return;
    }

    return groupBy(flags, (flag) => flag.type);
  }

  getRoute(routeString) {
    const { baseUrl, match } = this.props;
    const { marketId } = get(match, 'params');

    return `${baseUrl}/${marketId}/${routeString}`;
  }

  tabsToDisplay = (flagsByType = {}) => {
    const { newFieldReports = [] } = this.props;
    const displayedTabData = MARKET_TAB_DATA(newFieldReports);

    return displayedTabData;
  };

  handleChange(event, value) {
    this.setState({ value });
  }

  render() {
    const {
      currentWrangler: user, history, selectedMarket,
    } = this.props;

    let hasAccess;
    if (isUndefined(user)) {
      hasAccess = () => false;
    } else {
      hasAccess = user.access.can;
    }

    const { value, isLoadingMarkets } = this.state;

    const flagsByType = this.getFeatureFlags(selectedMarket);

    return (
      <Grid
        sx={{
          bgcolor: 'grey.100',
          // 56 and 64 — magic numbers for default heigths of AppBar
          minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
        }}
      >
        <AppBar position="static" color="default">
          <Tabs
            value={value}
            onChange={this.handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
          >
            {/* eslint-disable-next-line object-curly-newline */}
            {this.tabsToDisplay(flagsByType).map(({
              access, value: routeValue, label, icon,
            }) => (hasAccess(ACTIONS.READ, access) && (
              <Tab
                onClick={() => {
                  history.push(this.getRoute(routeValue));
                }}
                value={routeValue}
                label={label}
                icon={icon}
                classes={{ root: styles.tab }}
                key={routeValue}
              />
            )))}
          </Tabs>
        </AppBar>
        {value === DASHBOARD_TAB && hasAccess(ACTIONS.READ, PAGES.DASHBOARD) && (
          <DashboardPage />
        )}
        {value === VEHICLES_TAB && (
          hasAccess(ACTIONS.READ, PAGES.MARKET_VEHICLE_SUMMARY) && ( // skip showing default case
            <MarketVehicleSummary />
          )
        )}
        {value === REPAIRS_TAB && (
          <RepairTicketsSummary
            defaultFilter={[[], [], [], [], [], ['Open']]}
          />
        )}
        {value === FIELD_TAB && (
          <FieldReportTable
            defaultFilter={[[], [], [], [], [], [], [], [], [FieldReportStatuses.OPEN]]}
          />
        )}
        {value === LOCATIONS_TAB && (
          <LocationTable />
        )}
        {value === ZONES_TAB && (
          <ZonesTable />
        )}
        {value === VEHICLE_ZONES_TAB && (
          <VehicleZonesContainer />
        )}
        {value === RUN_MANAGER_TAB && (
          <MarketRunTables />
        )}
        {value === USERS_TAB && (
          <UserTable />
        )}
        {value === SETTINGS_TAB && (
          <MarketEditPage
            editing
            defaultSortName="End"
            defaultSortDirection="asc"
          />
        )}
      </Grid>
    );
  }
}
MarketTabs.propTypes = {
  baseUrl: PropTypes.string,
};

MarketTabs.defaultProps = {
  baseUrl: '/operator/markets',
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...vehicleActions,
  ...marketActions,
  ...fieldReportActions,
}, dispatch);

const mapStateToProps = ({ markets, wranglers, fieldReports }) => ({
  markets: markets.markets,
  selectedMarket: markets.selectedMarket,
  currentWrangler: wranglers.currentWrangler,
  newFieldReports: fieldReports.newFieldReports,
});

export const BaseMarketTabs = MarketTabs;
export default connect(mapStateToProps, mapDispatchToProps)(MarketTabs);
export {
  VEHICLES_TAB, REPAIRS_TAB,
  ALERTS_TAB, FIELD_TAB, LOCATIONS_TAB, ZONES_TAB,
  MAP_TAB, USERS_TAB, SETTINGS_TAB, DASHBOARD_TAB,
  VEHICLE_ZONES_TAB, RUN_MANAGER_TAB,
};
